import React from 'react';
import { useCubeQuery } from '@cubejs-client/react';
import { DateRangePickerValue } from 'ui/MuiDateRangePicker';
import { Filter } from '@cubejs-client/core';
import { differenceInCalendarDays, sub } from 'date-fns';
import { isEmptyString, isString } from 'lib/typeguards';
import {
    AUDIENCE_FREQUENCY_ANALYTICS_MEASURES,
    AUDIENCE_TOTALS_ANALYTICS_MEASURES,
    EAudienceMeasureView,
    getAudienceAnalyticsTimeDimensions,
    getAudienceMeasuresListWithRetroData,
    getCubeAudienceAnalyticsFilters
} from 'components/analytics/audiences/model';

export function useViewAudienceAnalyticsQueries(audienceId: string, dateRange: DateRangePickerValue) {
    const filters = React.useMemo<Filter[]>(() => {
        if (!isString(audienceId) || isEmptyString(audienceId)) {
            return [];
        }
        return getCubeAudienceAnalyticsFilters(undefined, [audienceId]);
    }, [audienceId]);
    const [retroStartDate, retroEndDate] = React.useMemo(() => {
        const difference = differenceInCalendarDays(dateRange.endDate, dateRange.startDate);
        return [sub(dateRange.startDate, { days: difference + 1 }), sub(dateRange.startDate, { days: 1 })];
    }, [dateRange.endDate, dateRange.startDate]);
    const datePoints = React.useMemo<[Date, Date, Date]>(
        () => [retroStartDate, dateRange.startDate, dateRange.endDate],
        [dateRange.endDate, dateRange.startDate, retroStartDate]
    );
    const audienceTotalsQuery = useCubeQuery({
        measures: AUDIENCE_TOTALS_ANALYTICS_MEASURES,
        dimensions: [],
        timeDimensions: getAudienceAnalyticsTimeDimensions(EAudienceMeasureView.AUDIENCE_VIEW, dateRange),
        filters
    });
    const audienceTotalsRetrospectiveQuery = useCubeQuery({
        measures: AUDIENCE_TOTALS_ANALYTICS_MEASURES,
        dimensions: [],
        timeDimensions: getAudienceAnalyticsTimeDimensions(EAudienceMeasureView.AUDIENCE_VIEW, {
            startDate: retroStartDate,
            endDate: retroEndDate
        }),
        filters
    });
    const audienceFrequencyQuery = useCubeQuery({
        measures: AUDIENCE_FREQUENCY_ANALYTICS_MEASURES,
        dimensions: [],
        timeDimensions: getAudienceAnalyticsTimeDimensions(EAudienceMeasureView.AUDIENCE_VIEW, dateRange),
        filters
    });
    const audienceFrequencyRetrospectiveQuery = useCubeQuery({
        measures: AUDIENCE_FREQUENCY_ANALYTICS_MEASURES,
        dimensions: [],
        timeDimensions: getAudienceAnalyticsTimeDimensions(EAudienceMeasureView.AUDIENCE_VIEW, {
            startDate: retroStartDate,
            endDate: retroEndDate
        }),
        filters
    });

    const currentAudienceQueries = React.useMemo(
        () => [audienceTotalsQuery, audienceFrequencyQuery],
        [audienceFrequencyQuery, audienceTotalsQuery]
    );
    const retroAudienceQueries = React.useMemo(
        () => [audienceTotalsRetrospectiveQuery, audienceFrequencyRetrospectiveQuery],
        [audienceFrequencyRetrospectiveQuery, audienceTotalsRetrospectiveQuery]
    );
    const [measureValues] = React.useMemo(
        () => getAudienceMeasuresListWithRetroData(currentAudienceQueries, retroAudienceQueries, true),
        [currentAudienceQueries, retroAudienceQueries]
    );
    const error = React.useMemo(
        () => [...currentAudienceQueries, ...retroAudienceQueries].find(query => query.error),
        [currentAudienceQueries, retroAudienceQueries]
    );
    return {
        error,
        datePoints,
        measureValues
    };
}
